/**
 * HelpWidget — floating "?" button + slide-out docs panel.
 *
 * Primary user-facing help surface for authenticated admin views. Built
 * instead of embedding Intercom (expensive SaaS + extra GDPR surface)
 * because our docs are already in-app as /docs/* pages (Sprint 3 Day 2).
 *
 * Behavior:
 * - Floating "?" button at bottom-right, stacked ABOVE ReportIssueButton
 *   (which lives at bottom:16; this sits above with 8px gap)
 * - Click opens a 360px-wide slide-out panel from the right edge
 * - Panel contains: search → filtered docs list → contact support
 * - Keyboard: "?" focuses search (guarded against form fields);
 *   Esc closes; click-outside closes
 * - PostHog events fire on open + on debounced search queries so Sprint
 *   4 dashboards can show WHICH help content users seek
 *
 * Design decisions logged in
 * docs/sprint-retrospectives/2026-04-19-sprint-3-day-5.md
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildSupportMailto } from '../utils/supportMailto';
import { DOC_PAGES } from '../pages/docs/docsStyles';
import { trackEvent } from '../services/posthog';

const PANEL_WIDTH = 360;
const INK = '#0a0a0a';
const MUTED = '#6b7280';
const PRIMARY = '#5DCAA5';

export default function HelpWidget() {
  const { user, effectiveRole, orgId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const queryEventRef = useRef(null);

  // Hide on non-app routes — prospects use the landing /docs directly
  const hidden = !user || /^\/(login|signup|legal|sla|__verify)/.test(location.pathname);

  // Match docs by title + description + keywords (description + keywords
  // added to docsStyles.js in Day 5; falls back to title-only if missing).
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DOC_PAGES;
    return DOC_PAGES.filter(p => {
      const hay = [p.title, p.description, ...(p.keywords || [])]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  const onOpen = useCallback(() => {
    setOpen(true);
    trackEvent('help_widget_opened', { path: location.pathname });
  }, [location.pathname]);

  const onClose = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // Debounced search event — fire at most once per 400ms so PostHog sees
  // the meaningful query, not every keystroke. Only if query is non-empty.
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) return;
    clearTimeout(queryEventRef.current);
    queryEventRef.current = setTimeout(() => {
      trackEvent('help_search_query', {
        query_length: query.length,
        result_count: results.length,
      });
    }, 400);
    return () => clearTimeout(queryEventRef.current);
  }, [query, open, results.length]);

  // Keyboard handling
  useEffect(() => {
    function onKey(e) {
      // Open on "?" — but ONLY when user isn't typing in a form field.
      // Otherwise we hijack every question mark in every textarea.
      if (!open && e.key === '?' && !e.metaKey && !e.ctrlKey) {
        const active = document.activeElement;
        const tag = active?.tagName;
        const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable;
        if (!isTyping) {
          e.preventDefault();
          onOpen();
        }
        return;
      }
      if (open && e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpen, onClose]);

  // Focus search input when panel opens
  useEffect(() => {
    if (open) queueMicrotask(() => inputRef.current?.focus());
  }, [open]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (panelRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      onClose();
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open, onClose]);

  if (hidden) return null;

  const supportMailto = buildSupportMailto({
    orgId,
    role: effectiveRole,
    path: location.pathname,
    subject: 'Juuni — question from Help panel',
    intro: "Hey — question:\n\n(describe what you're trying to do)",
  });

  return (
    <>
      {/* Floating "?" button — stacked above ReportIssueButton */}
      <button
        ref={buttonRef}
        type="button"
        aria-label="Open help"
        aria-expanded={open}
        title="Help — docs, shortcuts, support (press ?)"
        onClick={() => (open ? onClose() : onOpen())}
        style={{
          position: 'fixed',
          bottom: 56,   // 16 + ReportIssueButton(~32) + 8 gap
          right: 16,
          zIndex: 40,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: open ? PRIMARY : 'rgba(10,10,10,0.82)',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: 18,
          fontWeight: 600,
          fontFamily: "'Inter', -apple-system, sans-serif",
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          opacity: open ? 1 : 0.7,
          transition: 'opacity 0.2s, background 0.2s, transform 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = open ? '1' : '0.7';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        ?
      </button>

      {/* Slide-out panel */}
      {open && (
        <>
          {/* Scrim — subtle backdrop that doesn't trap clicks (those are
              handled by the click-outside effect above) */}
          <div style={{
            position: 'fixed', inset: 0, zIndex: 39,
            background: 'rgba(10,10,10,0.08)',
            pointerEvents: 'none',
          }} aria-hidden="true" />

          <aside
            ref={panelRef}
            role="dialog"
            aria-label="Help"
            aria-modal="false"
            style={{
              position: 'fixed',
              top: 16,
              right: 16,
              bottom: 16,
              width: PANEL_WIDTH,
              maxWidth: 'calc(100vw - 32px)',
              zIndex: 9000,
              background: '#FDFBF7',
              borderRadius: 14,
              boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: "'Inter', -apple-system, sans-serif",
              animation: 'juuniHelpSlideIn 0.18s ease-out',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 18px 12px',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>Help</div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close help"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 18, color: MUTED, padding: 2, lineHeight: 1,
                }}
              >×</button>
            </div>

            {/* Search */}
            <div style={{ padding: '12px 16px' }}>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search docs…"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 13,
                  borderRadius: 8,
                  border: '1px solid rgba(0,0,0,0.1)',
                  background: '#fff',
                  outline: 'none',
                  color: INK,
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Results */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 12px' }}>
              {results.length === 0 && (
                <div style={{
                  padding: '20px 12px', textAlign: 'center',
                  fontSize: 12, color: MUTED, lineHeight: 1.5,
                }}>
                  No docs match "{query}".<br />
                  Try emailing support below.
                </div>
              )}
              {results.map((p) => (
                <button
                  key={p.path}
                  type="button"
                  onClick={() => {
                    trackEvent('help_doc_opened', { path: p.path });
                    navigate(p.path);
                    onClose();
                  }}
                  style={{
                    display: 'block', width: '100%',
                    textAlign: 'left', background: 'transparent',
                    border: '1px solid transparent',
                    padding: '10px 12px', borderRadius: 8,
                    cursor: 'pointer', color: INK,
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    fontSize: 13,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.borderColor = 'rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 2 }}>{p.title}</div>
                  {p.description && (
                    <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.45 }}>
                      {p.description}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Footer — support contact */}
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid rgba(0,0,0,0.06)',
              background: '#fff',
            }}>
              <a
                href={supportMailto}
                onClick={() => trackEvent('help_support_clicked', { path: location.pathname })}
                style={{
                  display: 'block', padding: '10px 14px',
                  borderRadius: 8, background: INK, color: '#fff',
                  textDecoration: 'none', fontSize: 13, fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                Email support@juuniai.com
              </a>
              <div style={{
                marginTop: 8, fontSize: 10, color: MUTED,
                textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
              }}>
                Press <kbd style={kbdStyle}>?</kbd> to open · <kbd style={kbdStyle}>⌘K</kbd> for commands
              </div>
            </div>
          </aside>

          <style>{`
            @keyframes juuniHelpSlideIn {
              from { transform: translateX(12px); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          `}</style>
        </>
      )}
    </>
  );
}

const kbdStyle = {
  display: 'inline-block',
  padding: '1px 5px',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  background: '#F3F0E8',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 3,
  color: INK,
};
